import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, Spinner} from 'react-bootstrap'
import clsx from 'clsx'
import {useQuery} from 'react-query'
import toaster from '../../../../Utils/toaster'
import {useProfileImageUrl} from '../../../../Utils/fileUrlHelper'
import {CommonPaginationModel} from '../../../../Utils/commonPagination'
import {AIInterpretationSearchBar} from './ai-interpretation/AIInterpretationSearchBar'
import {AIInterpretationFilterDropdown} from './ai-interpretation/AIInterpretationFilterDropdown'
import {fetchAIInterpretationRequestors, saveAIInterpretationPermissions} from './ai-interpretation/api'
import {
  AIInterpretationFilterModel,
  EMPTY_AI_INTERPRETATION_FILTER,
  PaginationInfo,
  RequestorRow,
} from './ai-interpretation/types'
import './AIInterpretationSettings.css'

interface RequestorAvatarProps {
  firstName: string
  lastName: string
  avatar?: string | null
}

function RequestorAvatar({firstName, lastName, avatar}: RequestorAvatarProps) {
  const avatarUrl = useProfileImageUrl(avatar ?? undefined)
  const initials =
    (firstName?.charAt(0) ?? '').toUpperCase() + (lastName?.charAt(0) ?? '').toUpperCase()

  if (avatarUrl) {
    return (
      <div className='ai-interp-settings__avatar overflow-hidden'>
        <img src={avatarUrl} alt={initials} className='w-100 h-100 object-fit-cover' />
      </div>
    )
  }

  return <div className='ai-interp-settings__avatar'>{initials}</div>
}

const DEFAULT_PAGINATION: PaginationInfo = {
  page: 1,
  from: 0,
  to: 0,
  total: 0,
  last_page: 1,
  items_per_page: 10,
}

export function AIInterpretationSettingsView({customerId}: {customerId: number}) {
  const [appliedSearch, setAppliedSearch] = useState('')
  const [filter, setFilter] = useState<AIInterpretationFilterModel>({...EMPTY_AI_INTERPRETATION_FILTER})
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [savedPermissions, setSavedPermissions] = useState<Record<string, boolean>>({})
  const [draft, setDraft] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState(false)

  const queryKey = useMemo(
    () => ['ai-interpretation-requestors', customerId, page, rowsPerPage, appliedSearch, filter],
    [customerId, page, rowsPerPage, appliedSearch, filter]
  )

  const {data, isFetching, refetch} = useQuery(
    queryKey,
    () =>
      fetchAIInterpretationRequestors(
        customerId,
        {page, items_per_page: rowsPerPage, search: appliedSearch, sort: null, order: 'desc'},
        filter
      ),
    {
      enabled: customerId > 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  )

  const rows = data?.rows ?? []
  const pagination = data?.pagination ?? DEFAULT_PAGINATION

  useEffect(() => {
    if (!rows.length) return
    setSavedPermissions((prev) => {
      const next = {...prev}
      rows.forEach((row) => {
        next[row.userId] = row.aiInterpretationPermission
      })
      return next
    })
    setDraft((prev) => {
      const next = {...prev}
      rows.forEach((row) => {
        if (next[row.userId] === undefined) {
          next[row.userId] = row.aiInterpretationPermission
        }
      })
      return next
    })
  }, [rows])

  const isDirty = useMemo(
    () => Object.keys(draft).some((userId) => draft[userId] !== savedPermissions[userId]),
    [draft, savedPermissions]
  )

  const handleSearch = useCallback((term: string) => {
    setAppliedSearch(term)
    setPage(1)
  }, [])

  const handleFilterApply = useCallback((nextFilter: AIInterpretationFilterModel) => {
    setFilter(nextFilter)
    setPage(1)
  }, [])

  const handlePageChange = useCallback(
    (nextPage: number) => {
      if (isDirty) {
        const proceed = window.confirm(
          'You have unsaved permission changes. Continue without saving?'
        )
        if (!proceed) return
      }
      setPage(nextPage)
    },
    [isDirty]
  )

  const handleRowsPerPageChange = useCallback(
    (nextRowsPerPage: number) => {
      if (isDirty) {
        const proceed = window.confirm(
          'You have unsaved permission changes. Continue without saving?'
        )
        if (!proceed) return
      }
      setRowsPerPage(nextRowsPerPage)
      setPage(1)
    },
    [isDirty]
  )

  const togglePermission = (userId: string) => {
    setDraft((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }))
  }

  const allPageChecked = useMemo(
    () => rows.length > 0 && rows.every((row) => draft[row.userId] === true),
    [rows, draft]
  )

  const somePageChecked = useMemo(
    () => rows.some((row) => draft[row.userId] === true),
    [rows, draft]
  )

  const selectAllRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = somePageChecked && !allPageChecked
    }
  }, [somePageChecked, allPageChecked])

  const toggleAllOnPage = useCallback(() => {
    const nextValue = !allPageChecked
    setDraft((prev) => {
      const next = {...prev}
      rows.forEach((row) => {
        next[row.userId] = nextValue
      })
      return next
    })
  }, [allPageChecked, rows])

  const resetDraft = useCallback(() => {
    setDraft({...savedPermissions})
  }, [savedPermissions])

  const save = async () => {
    if (!customerId || !isDirty) return
    setSaving(true)
    try {
      const permissions = Object.keys(draft)
        .filter((userId) => draft[userId] !== savedPermissions[userId])
        .map((userId) => ({
          userId,
          aiInterpretationPermission: draft[userId] ?? false,
        }))

      await saveAIInterpretationPermissions(customerId, permissions)

      setSavedPermissions((prev) => {
        const next = {...prev}
        permissions.forEach((entry) => {
          next[entry.userId] = entry.aiInterpretationPermission
        })
        return next
      })

      toaster('success', 'Permissions saved')
      refetch()
    } catch (error: any) {
      console.error(error)
      const message = error?.response?.data?.message ?? 'Failed to save permissions'
      toaster('error', message)
    } finally {
      setSaving(false)
    }
  }

  const totalPages = Math.max(1, pagination.last_page || 1)

  return (
    <div className='ai-interp-settings'>
      <div className='ai-interp-settings__toolbar d-flex align-items-center justify-content-end flex-wrap gap-3 mb-4'>
        <AIInterpretationSearchBar onSearch={handleSearch} />
        <AIInterpretationFilterDropdown value={filter} onApply={handleFilterApply} />
      </div>

      {isFetching && rows.length === 0 ? (
        <div className='d-flex justify-content-center align-items-center py-10'>
          <Spinner animation='border' role='status' />
        </div>
      ) : (
        <>
          <div
            className={clsx(
              'ai-interp-settings__table-wrap',
              isFetching && 'ai-interp-settings__table-wrap--loading'
            )}
          >
            <table className='ai-interp-settings__table'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>IVR PIN</th>
                  <th>Role</th>
                  <th>Customer</th>
                  <th className='ai-interp-settings__actions-col text-center'>
                    {rows.length > 0 && (
                      <input
                        ref={selectAllRef}
                        type='checkbox'
                        className={clsx('ai-interp-settings__checkbox', 'form-check-input')}
                        checked={allPageChecked}
                        onChange={toggleAllOnPage}
                        aria-label='Check or uncheck all requestors on this page'
                      />
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5}>
                      <div className='ai-interp-settings__empty'>
                        No requestors found for this customer.
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.userId}>
                      <td>
                        <div className='d-flex align-items-center gap-3'>
                          <RequestorAvatar
                            firstName={row.firstName}
                            lastName={row.lastName}
                            avatar={row.avatar}
                          />
                          <div>
                            <div className='ai-interp-settings__name'>
                              {row.firstName} {row.lastName}
                            </div>
                            <div className='ai-interp-settings__email'>{row.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='ai-interp-settings__cell-text'>{row.pinCodeString || '-'}</span>
                      </td>
                      <td>
                        <span className='ai-interp-settings__cell-text'>{row.role || '-'}</span>
                      </td>
                      <td>
                        <span className='ai-interp-settings__cell-text'>{row.customerName || '-'}</span>
                      </td>
                      <td className='text-center'>
                        <input
                          type='checkbox'
                          className={clsx('ai-interp-settings__checkbox', 'form-check-input')}
                          checked={draft[row.userId] ?? false}
                          onChange={() => togglePermission(row.userId)}
                          aria-label={`AI interpretation permission for ${row.firstName} ${row.lastName}`}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div className='ai-interp-settings__pagination mt-4'>
              <CommonPaginationModel
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                total={pagination.total}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </div>
          )}

          <div className='ai-interp-settings__footer'>
            <Button
              variant='light'
              className='ai-interp-settings__reset-btn'
              onClick={resetDraft}
              disabled={!isDirty || saving}
            >
              Reset
            </Button>
            <Button
              className='ai-interp-settings__save-btn'
              onClick={save}
              disabled={saving || !isDirty}
            >
              {saving ? (
                <>
                  <Spinner animation='border' size='sm' className='me-2' />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
